import React, { useState } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Search,
  Calendar,
  Clock,
  ArrowRight,
  TrendingUp
} from "lucide-react";

export default function Blog() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const { data: articles = [], isLoading } = useQuery({
    queryKey: ['articles'],
    queryFn: () => base44.entities.Article.list('-created_date')
  });

  const categories = ["all", "MLM Crypto", "DeFi", "Trading", "Investissement", "Formation"];

  const filteredArticles = articles.filter(article => {
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || article.category === selectedCategory;
    return matchesSearch && matchesCategory && article.published;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 to-blue-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4">
          <Badge className="mb-6 bg-blue-500/20 text-blue-200 border-blue-400">
            📚 Ressources & Guides
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Blog AmsWeb
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            Guides pratiques, analyses d'opportunités, et stratégies pour réussir 
            dans le MLM crypto et la DeFi
          </p>
        </div>
      </section>

      {/* Search & Filters */}
      <section className="py-8 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="flex-1 relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Rechercher un article..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {categories.map(cat => (
                <Badge
                  key={cat}
                  variant={selectedCategory === cat ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => setSelectedCategory(cat)}
                >
                  {cat === "all" ? "Tous" : cat}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          {isLoading && (
            <div className="text-center py-12">
              <p className="text-slate-600">Chargement des articles...</p>
            </div>
          )}

          {!isLoading && filteredArticles.length > 0 && (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article) => (
                <Card key={article.id} className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group cursor-pointer">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={article.image_url || "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=800&h=500&fit=crop"} 
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                    <Badge className="absolute top-4 left-4 bg-blue-600">
                      {article.category}
                    </Badge>
                  </div>
                  <CardHeader>
                    <div className="flex items-center gap-4 text-sm text-slate-600 mb-3">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(article.created_date).toLocaleDateString('fr-FR')}
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {article.read_time} min
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 group-hover:text-blue-600 transition-colors">
                      {article.title}
                    </h3>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-600 mb-4 line-clamp-2">{article.excerpt}</p>
                    <Link to={`/blog/${article.slug}`} className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:gap-3 transition-all">
                      Lire l'article
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {!isLoading && filteredArticles.length === 0 && (
            <div className="text-center py-16">
              <p className="text-slate-600 text-lg mb-4">Aucun article trouvé</p>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-16 bg-gradient-to-br from-blue-900 to-purple-900 text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <TrendingUp className="w-12 h-12 mx-auto mb-6 text-blue-400" />
          <h2 className="text-3xl font-bold mb-4">
            Recevez nos Meilleurs Articles
          </h2>
          <p className="text-blue-100 mb-8">
            1 email par semaine avec nos analyses exclusives et opportunités MLM crypto
          </p>
          <Link to={createPageUrl("Contact")}>
            <Button size="lg" className="bg-white text-blue-900 hover:bg-blue-50">
              S'inscrire à la Newsletter
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
